import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Zap, Trash2, Clock, Users, Play, ChevronRight,
  ArrowDown, CheckCircle2, Loader2, UserPlus, Tag
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_STEP = { name: "", subject: "", body: "", delay_hours: 24 };

function StepCard({ step, idx, onChange, onDelete, isFirst }) {
  return (
    <div className="relative">
      {idx > 0 && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 flex flex-col items-center">
          <div className="w-px h-4" style={{ background: "var(--border)" }} />
          <ArrowDown className="w-3.5 h-3.5" style={{ color: "var(--text-xsoft)" }} />
        </div>
      )}
      <div className="card-premium p-4 rounded-xl mt-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center text-white shrink-0" style={{ background: "var(--brand)" }}>
            {idx + 1}
          </div>
          <div className="flex-1 min-w-0">
            <Input
              value={step.name}
              onChange={e => onChange(idx, "name", e.target.value)}
              placeholder={`Step ${idx + 1} name (e.g. Welcome email)`}
              className="h-7 text-sm"
              style={{ background: "var(--bg)" }}
            />
          </div>
          {!isFirst && (
            <div className="flex items-center gap-1 shrink-0">
              <Clock className="w-3.5 h-3.5" style={{ color: "var(--text-soft)" }} />
              <Input
                type="number"
                min="0"
                value={step.delay_hours}
                onChange={e => onChange(idx, "delay_hours", parseInt(e.target.value) || 0)}
                className="w-16 h-7 text-xs"
                style={{ background: "var(--bg)" }}
              />
              <span className="text-xs" style={{ color: "var(--text-soft)" }}>h delay</span>
            </div>
          )}
          {isFirst && (
            <span className="text-xs shrink-0 px-2 py-0.5 rounded-full" style={{ background: "var(--brand-dim)", color: "var(--brand)" }}>Immediate</span>
          )}
          <button onClick={() => onDelete(idx)} className="p-1 rounded hover:opacity-80">
            <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--text-xsoft)" }} />
          </button>
        </div>
        <div className="space-y-2">
          <Input
            value={step.subject}
            onChange={e => onChange(idx, "subject", e.target.value)}
            placeholder="Subject line (supports {{first_name}}, {{company}})"
            className="text-sm"
          />
          <Textarea
            value={step.body}
            onChange={e => onChange(idx, "body", e.target.value)}
            placeholder="Email body (HTML supported)"
            rows={4}
            className="text-xs font-sans"
          />
        </div>
      </div>
    </div>
  );
}

export default function Sequences() {
  const [sequences, setSequences] = useState([]);
  const [selected, setSelected] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [buildOpen, setBuildOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollSeq, setEnrollSeq] = useState(null);
  const [enrollForm, setEnrollForm] = useState({ mode: "email", value: "" });
  const [enrolling, setEnrolling] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", steps: [{ ...EMPTY_STEP, delay_hours: 0 }] });
  const [saving, setSaving] = useState(false);
  const [editStepsOpen, setEditStepsOpen] = useState(false);
  const [editForm, setEditForm] = useState({ steps: [] });
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/sequences");
    setSequences(data);
  };
  useEffect(() => { load(); }, []);

  const loadEnrollments = async (sid) => {
    const { data } = await api.get(`/sequences/${sid}/enrollments`);
    setEnrollments(data);
  };

  const selectSeq = (s) => {
    setSelected(s);
    loadEnrollments(s.id);
  };

  const addStep = () => setForm(f => ({ ...f, steps: [...f.steps, { ...EMPTY_STEP }] }));

  const updateStep = (idx, field, val) => {
    setForm(f => ({ ...f, steps: f.steps.map((s, i) => i === idx ? { ...s, [field]: val } : s) }));
  };

  const deleteStep = (idx) => {
    if (form.steps.length <= 1) { toast.error("Need at least 1 step"); return; }
    setForm(f => ({ ...f, steps: f.steps.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.name) { toast.error("Sequence name required"); return; }
    if (form.steps.some(s => !s.subject)) { toast.error("All steps need a subject"); return; }
    setSaving(true);
    try {
      await api.post("/sequences", form);
      toast.success("Sequence created");
      setBuildOpen(false);
      setForm({ name: "", description: "", steps: [{ ...EMPTY_STEP, delay_hours: 0 }] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const deleteSeq = async (id) => {
    if (!window.confirm("Delete this sequence and all its enrollments?")) return;
    await api.delete(`/sequences/${id}`);
    if (selected?.id === id) setSelected(null);
    toast.success("Deleted");
    load();
  };

  const openEnroll = (seq) => { setEnrollSeq(seq); setEnrollForm({ mode: "email", value: "" }); setEnrollOpen(true); };

  const doEnroll = async () => {
    if (!enrollForm.value) { toast.error("Enter a value"); return; }
    setEnrolling(true);
    try {
      const payload = enrollForm.mode === "email"
        ? { contact_emails: [enrollForm.value] }
        : { tag: enrollForm.value };
      const { data } = await api.post(`/sequences/${enrollSeq.id}/enroll`, payload);
      toast.success(`${data.enrolled} contact(s) enrolled`);
      setEnrollOpen(false);
      if (selected?.id === enrollSeq.id) loadEnrollments(enrollSeq.id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enrollment failed"); }
    finally { setEnrolling(false); }
  };

  const cancelEnrollment = async (sid, eid) => {
    await api.delete(`/sequences/${sid}/enrollments/${eid}`);
    toast.success("Enrollment cancelled");
    if (selected) loadEnrollments(selected.id);
  };

  const stepDayLabels = (steps) => {
    let cumHours = 0;
    return (steps || []).map((s, i) => {
      if (i > 0) cumHours += s.delay_hours || 0;
      const days = cumHours / 24;
      const label = i === 0 ? "Day 0 · Immediate" : days < 1 ? `+${s.delay_hours}h later` : `Day ${days % 1 === 0 ? days : days.toFixed(1)}`;
      return { ...s, _label: label };
    });
  };

  const openEditSteps = () => {
    setEditForm({ steps: (selected.steps || []).map(s => ({ ...s })) });
    setEditStepsOpen(true);
  };
  const editAddStep = () => setEditForm(f => ({ ...f, steps: [...f.steps, { ...EMPTY_STEP }] }));
  const editUpdateStep = (idx, field, val) => setEditForm(f => ({ ...f, steps: f.steps.map((s, i) => i === idx ? { ...s, [field]: val } : s) }));
  const editDeleteStep = (idx) => {
    if (editForm.steps.length <= 1) { toast.error("Need at least 1 step"); return; }
    setEditForm(f => ({ ...f, steps: f.steps.filter((_, i) => i !== idx) }));
  };
  const saveEditedSteps = async () => {
    if (editForm.steps.some(s => !s.subject)) { toast.error("All steps need a subject"); return; }
    setEditSaving(true);
    try {
      await api.patch(`/sequences/${selected.id}`, { steps: editForm.steps });
      toast.success("Sequence timeline updated");
      setEditStepsOpen(false);
      const { data } = await api.get("/sequences");
      setSequences(data);
      setSelected(data.find(s => s.id === selected.id) || null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setEditSaving(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="sequences-page">
      {/* Header */}
      <div className="rise flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>Drip Sequences</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>Multi-step timed email sequences for onboarding, nurturing, and sales.</p>
        </div>
        <Button onClick={() => setBuildOpen(true)} className="gap-2 text-white" style={{ background: "var(--brand)" }} data-testid="new-sequence-btn">
          <Plus className="w-4 h-4" /> New Sequence
        </Button>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Sequences", value: sequences.length, color: "var(--brand)" },
          { label: "Active enrollments", value: sequences.reduce((sum, s) => sum + (s.enrollment_count || 0), 0), color: "var(--success)" },
          { label: "Total enrolled ever", value: sequences.reduce((sum, s) => sum + (s.total_enrolled || 0), 0), color: "var(--text)" },
        ].map(({ label, value, color }) => (
          <Card key={label} className="rise card-premium p-4 flex items-center gap-3" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="text-2xl font-bold" style={{ color, fontFamily: "var(--font-sans)" }}>{value}</div>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-soft)" }}>{label}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sequence list */}
        <div className="space-y-3 lg:col-span-1">
          {sequences.length === 0 ? (
            <Card className="card-premium p-12 text-center" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <Zap className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: "var(--text)" }} />
              <div className="font-semibold mb-1" style={{ color: "var(--text)" }}>No sequences yet</div>
              <p className="text-xs" style={{ color: "var(--text-soft)" }}>Build your first drip to automate follow-ups.</p>
            </Card>
          ) : (
            sequences.map(seq => (
              <Card
                key={seq.id}
                data-testid={`seq-card-${seq.id}`}
                onClick={() => selectSeq(seq)}
                className="card-premium p-4 cursor-pointer hover:shadow-md transition-all"
                style={{
                  background: "var(--surface)", borderColor: selected?.id === seq.id ? "var(--brand)" : "var(--border)",
                  boxShadow: selected?.id === seq.id ? "0 0 0 2px var(--brand)" : undefined,
                }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate" style={{ color: "var(--text)" }}>{seq.name}</div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--text-soft)" }}>
                      {(seq.steps || []).length} steps · {seq.enrollment_count || 0} active
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); openEnroll(seq); }}
                      className="text-white h-7 text-xs gap-1" style={{ background: "var(--brand)" }}>
                      <Play className="w-3 h-3" /> Enroll
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); deleteSeq(seq.id); }}>
                      <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--text-xsoft)" }} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Enrollments detail */}
        <div className="lg:col-span-2">
          {selected ? (
            <Card className="card-premium overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="p-5 border-b flex items-start justify-between gap-3" style={{ borderColor: "var(--border)" }}>
                <div>
                  <div className="font-bold text-lg" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>{selected.name}</div>
                  <div className="text-sm mt-0.5" style={{ color: "var(--text-soft)" }}>{(selected.steps || []).length} steps · {enrollments.length} total enrollments</div>
                </div>
                <Button size="sm" variant="outline" onClick={openEditSteps} data-testid="edit-steps-btn" className="gap-1.5 shrink-0">
                  <Clock className="w-3.5 h-3.5" /> Edit Timeline
                </Button>
              </div>

              {/* Drip timeline */}
              <div className="p-5 border-b" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
                <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--text-soft)" }}>Timeline</div>
                <div className="flex flex-wrap items-stretch gap-2" data-testid="sequence-timeline">
                  {stepDayLabels(selected.steps).map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="rounded-xl px-3 py-2 min-w-[150px]" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} data-testid={`timeline-step-${i}`}>
                        <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: "var(--brand)" }}>
                          <Clock className="w-3 h-3" /> {s._label}
                        </div>
                        <div className="text-sm font-medium mt-1 truncate max-w-[160px]" style={{ color: "var(--text)" }}>{s.name || `Step ${i + 1}`}</div>
                        <div className="text-xs truncate max-w-[160px]" style={{ color: "var(--text-soft)" }}>{s.subject || "(no subject)"}</div>
                      </div>
                      {i < (selected.steps || []).length - 1 && <ChevronRight className="w-4 h-4 shrink-0" style={{ color: "var(--text-xsoft)" }} />}
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-5">
                <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--text-soft)" }}>Contacts enrolled ({enrollments.length})</div>
                {enrollments.length === 0 ? (
                  <div className="text-center py-10" style={{ color: "var(--text-xsoft)" }}>
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    No enrollments yet — click Enroll to add contacts
                  </div>
                ) : (
                  <div className="space-y-2">
                    {enrollments.map((e, i) => (
                      <div key={e.id} data-testid={`enrollment-${i}`}
                        className="flex items-center gap-3 p-3 rounded-xl"
                        style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: "var(--brand)" }}>
                          {(e.contact_name || e.contact_email)[0]?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate" style={{ color: "var(--text)" }}>{e.contact_name || e.contact_email}</div>
                          <div className="text-xs" style={{ color: "var(--text-soft)" }}>
                            Step {e.current_step + 1}/{(selected.steps || []).length} · {e.status}
                          </div>
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full"
                          style={{
                            background: e.status === "completed" ? "rgba(22,163,74,0.12)" : e.status === "cancelled" ? "rgba(239,68,68,0.10)" : "var(--brand-dim)",
                            color: e.status === "completed" ? "var(--success)" : e.status === "cancelled" ? "#EF4444" : "var(--brand)",
                          }}>
                          {e.status}
                        </span>
                        {e.status === "active" && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => cancelEnrollment(selected.id, e.id)}>
                            <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--text-xsoft)" }} />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          ) : (
            <Card className="card-premium p-12 text-center" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <ChevronRight className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: "var(--text)" }} />
              <div className="font-semibold mb-1" style={{ color: "var(--text)" }}>Select a sequence</div>
              <p className="text-xs" style={{ color: "var(--text-soft)" }}>View and manage enrollments for any sequence.</p>
            </Card>
          )}
        </div>
      </div>

      {/* Build sequence dialog */}
      <Dialog open={buildOpen} onOpenChange={setBuildOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Build Drip Sequence</DialogTitle>
            <DialogDescription>Create a series of timed emails. Each step fires after the configured delay.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Sequence name *</Label><Input data-testid="seq-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Onboarding series, Win-back campaign…" /></div>
            <div><Label>Description</Label><Input data-testid="seq-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional description" /></div>
            <div className="border-t pt-4" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center justify-between mb-2">
                <Label>Email Steps ({form.steps.length})</Label>
                <Button size="sm" variant="outline" onClick={addStep} className="gap-1 h-7 text-xs">
                  <Plus className="w-3 h-3" /> Add step
                </Button>
              </div>
              {form.steps.map((step, idx) => (
                <StepCard
                  key={idx}
                  step={step}
                  idx={idx}
                  onChange={updateStep}
                  onDelete={deleteStep}
                  isFirst={idx === 0}
                />
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="text-white gap-2" style={{ background: "var(--brand)" }} data-testid="seq-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><CheckCircle2 className="w-4 h-4" /> Create Sequence</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enroll dialog */}
      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enroll contacts</DialogTitle>
            <DialogDescription>Add contacts to "{enrollSeq?.name}". They'll receive emails as scheduled.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Enroll by</Label>
              <Select value={enrollForm.mode} onValueChange={v => setEnrollForm({ ...enrollForm, mode: v, value: "" })}>
                <SelectTrigger data-testid="enroll-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email"><UserPlus className="w-3.5 h-3.5 inline mr-1" />Single email address</SelectItem>
                  <SelectItem value="tag"><Tag className="w-3.5 h-3.5 inline mr-1" />All contacts with a tag</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{enrollForm.mode === "email" ? "Email address" : "Tag name"}</Label>
              <Input
                data-testid="enroll-value"
                value={enrollForm.value}
                onChange={e => setEnrollForm({ ...enrollForm, value: e.target.value })}
                placeholder={enrollForm.mode === "email" ? "alex@company.com" : "e.g. lead, vip, warm"}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={doEnroll} disabled={enrolling} className="text-white gap-2" style={{ background: "var(--brand)" }} data-testid="enroll-submit-btn">
              {enrolling ? <><Loader2 className="w-4 h-4 animate-spin" /> Enrolling…</> : <><Play className="w-4 h-4" /> Start Sequence</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit sequence timeline dialog */}
      <Dialog open={editStepsOpen} onOpenChange={setEditStepsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit "{selected?.name}" Timeline</DialogTitle>
            <DialogDescription>Adjust each step's timing, subject and body. Changes apply to future sends.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <Label>Email Steps ({editForm.steps.length})</Label>
              <Button size="sm" variant="outline" onClick={editAddStep} className="gap-1 h-7 text-xs" data-testid="edit-steps-add">
                <Plus className="w-3 h-3" /> Add step
              </Button>
            </div>
            {editForm.steps.map((step, idx) => (
              <StepCard key={idx} step={step} idx={idx} onChange={editUpdateStep} onDelete={editDeleteStep} isFirst={idx === 0} />
            ))}
          </div>
          <DialogFooter>
            <Button onClick={saveEditedSteps} disabled={editSaving} className="text-white gap-2" style={{ background: "var(--brand)" }} data-testid="edit-steps-save-btn">
              {editSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><CheckCircle2 className="w-4 h-4" /> Save Timeline</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
